import React, { useState, useRef, useEffect, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import '../styles/GamerCat.css';

const GamerCat = () => {
  const { theme, isDarkMode } = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Meow! I'm GamerCat 🐱🎮 How can I help you today?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // FAQ and website information
  const getResponse = (userMessage) => {
    const message = userMessage.toLowerCase();

    // Greetings
    if (message.match(/\b(hi|hello|hey|meow)\b/)) {
      return "Meow! Hello there, gamer! 🎮 How can I assist you today?";
    }

    // Points and rewards
    if (message.includes('point') || message.includes('earn')) {
      return "You can earn points by:\n• Daily check-ins (build streaks!) 📅\n• Completing daily quests (5 new ones daily) ✅\n• Creating showcases (25 points) 🎨\n• Writing reviews (10 points) 📝\n• Posting comments 💬\n• Completing achievements 🏆\n• Adding discounts to the site\n• Being active in the community\nCheck the Points Guide page for detailed opportunities!";
    }

    // Daily rewards and quests
    if (message.includes('daily') || message.includes('quest') || message.includes('streak') || message.includes('check-in')) {
      return "Daily rewards keep you coming back! 📅\n• Check in daily to build your streak\n• Longer streaks = bigger rewards\n• 5 new daily quests appear each day\n• Quests reset at midnight (server time)\n• Complete all 5 for maximum points\n• Streak resets if you miss a day\n• Keep all earned points forever!\nVisit your dashboard to check in and view quests!";
    }

    // Giveaways
    if (message.includes('giveaway')) {
      return "Giveaways let you win free games! 🎁\n• Browse available giveaways\n• Complete requirements to enter (social tasks, points, etc.)\n• Winners selected randomly when it ends\n• Auto-payout sends keys automatically\n• Reroll system if winners don't claim\n• Check notifications for wins!\n• Can create giveaways if you're a creator\nVisit the Giveaways page to participate!";
    }

    // Shop
    if (message.includes('shop') || message.includes('buy')) {
      return "The Shop lets you spend your hard-earned points! 🛒\n• Purchase profile customizations\n• Buy special badges and titles\n• Get featured showcase slots\n• Unlock exclusive perks\nVisit the Shop page to see what's available!";
    }

    // Community Cards
    if (message.includes('community') || message.includes('card')) {
      return "Community Cards are your personal profile cards! 🎴\n• Create your digital gaming card\n• Add your name, bio, and avatar\n• Link your social media profiles\n• Use hashtags to get discovered\n• Get ratings and reviews from others\n• Must verify Steam, Discord & (Twitter OR Bluesky) to create\nShow off who you are on the Community Cards page!";
    }

    // Discounts
    if (message.includes('discount') || message.includes('deal')) {
      return "Find the best gaming deals! 💰\n• Community-shared discounts\n• Verified game deals\n• Steam, Epic, and more\n• Vote on the best deals\nCheck the Discounts page for current offers!";
    }

    // Indie Games
    if (message.includes('indie')) {
      return "Support indie game developers! 🎮\n• Discover new indie games\n• Read developer updates\n• Leave feedback and reviews\n• Connect with creators\nExplore the Indie Games page!";
    }

    // Profile and customization
    if (message.includes('profile') || message.includes('customize')) {
      return "Customize your gaming profile! 👤\n• Upload avatar and banner\n• Add bio and social links\n• Display achievements\n• Show your showcases\n• Create community card\nEdit your profile in Settings or Profile Edit page!";
    }

    // Showcase
    if (message.includes('showcase')) {
      return "Showcases let you share your gaming content! 🎨\n• Upload gaming setups\n• Share screenshots\n• Post artwork and clips\n• Add titles and descriptions\n• Max 5 images per showcase\n• Earn 25 points per showcase\n• Appears in your followers' feed\nCreate showcases on your profile page!";
    }

    // Feed
    if (message.includes('feed')) {
      return "Your personalized activity feed! 📰\n• See what people you follow post\n• Track showcase creations\n• See giveaway announcements\n• View indie game releases\n• Follow your favorite creators\nVisit the Feed page to stay updated!";
    }

    // Achievements
    if (message.includes('achievement') || message.includes('badge')) {
      return "Unlock achievements to show off! 🏆\n• Complete various tasks\n• Earn unique badges\n• Track your progress\n• Display on your profile\nCheck the Achievements page to see all available achievements!";
    }

    // Messages and chat
    if (message.includes('message') || message.includes('chat') || message.includes('dm')) {
      return "Connect with other gamers! 💬\n• Send direct messages\n• Real-time chat\n• Share images and files\n• Create conversations\nVisit the Messages page to start chatting!";
    }

    // Scammers database
    if (message.includes('scam')) {
      return "Stay safe from scammers! 🛡️\n• Search scammer database\n• Report suspicious users\n• Check Steam profiles\n• Community verification\nVisit the Scammers page to check profiles!";
    }

    // Code Requests
    if (message.includes('code request')) {
      return "Request game codes from the community! 🎮\n• Post code requests\n• Help others by sharing codes\n• Build community reputation\n• Get notified of fulfillments\nVisit the Code Requests page!";
    }

    // Reviews
    if (message.includes('review')) {
      return "Share your gaming opinions! ⭐\n• Write game reviews\n• Rate games\n• Help others decide\n• Earn points for reviews\nVisit the Game Reviews page!";
    }

    // Leaderboard
    if (message.includes('leaderboard') || message.includes('top')) {
      return "Compete with other gamers! 🏆\n• View top point earners\n• Check ranking categories\n• Monthly and all-time ranks\n• Climb the ladder!\nVisit the Leaderboard page to see where you stand!";
    }

    // Account and registration
    if (message.includes('register') || message.includes('sign up') || message.includes('account') || message.includes('verif')) {
      return "Join our gaming community! 🎮\n• Click 'Register' in the navigation\n• Create your account\n• Verify your email\n• Link Steam, Discord, and Twitter/Bluesky\n• Start earning points!\nNote: You need either Twitter OR Bluesky verification (your choice) along with Steam & Discord for full access!";
    }

    // Bluesky verification
    if (message.includes('bluesky') || message.includes('blue sky') || message.includes('app password')) {
      return "Bluesky is an alternative to Twitter! 🦋\n\nHow to verify:\n1. Go to Settings page\n2. Get an app password from Bluesky\n   (Settings → App Passwords → Add)\n3. Enter your handle and app password\n4. Verify instantly!\n\n• You only need ONE: Twitter OR Bluesky\n• Same verification level as Twitter\n• Works for all features\n• Your password is never stored!";
    }

    // Tools
    if (message.includes('tool')) {
      return "Useful gaming tools! 🔧\n• Steam profile analyzer\n• Game price trackers\n• Community utilities\nCheck out the Tools page!";
    }

    // Blog
    if (message.includes('blog') || message.includes('news')) {
      return "Stay updated with gaming news! 📰\n• Community blog posts\n• Gaming news and updates\n• Tips and guides\n• Community discussions\nVisit the Blog and News pages!";
    }

    // About and FAQ
    if (message.includes('about') || message.includes('what is')) {
      return "GamersUnion is a community platform for gamers! 🎮\n• Share and discover content\n• Participate in giveaways\n• Earn points and rewards\n• Connect with gamers worldwide\n• Stay safe from scammers\nVisit the About and FAQ pages for more info!";
    }

    // Help
    if (message.includes('help')) {
      return "I can help you with:\n• Points & daily rewards 💰\n• Giveaways 🎁\n• Community Cards 🎴\n• Showcases & Feed 📸\n• Shop items 🛒\n• Achievements 🏆\n• Indie Games 🎮\n• Scammer Database 🛡️\n• Reviews & Discounts\n• Profile customization\n\nJust ask me anything about the website!";
    }

    // Default response
    return "Meow! I'm not sure about that one. 🤔\nTry asking me about:\n• Points and rewards\n• Giveaways & how to enter\n• Community Cards (your profile card)\n• Showcases (share your content)\n• Shop items\n• Achievements\n• Indie Games\n• Or type 'help' for all options!";
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Get bot response
    setTimeout(() => {
      const botResponse = {
        type: 'bot',
        text: getResponse(inputValue),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 500);

    setInputValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const quickQuestions = [
    "How do I earn points?",
    "What are giveaways?",
    "Tell me about the shop",
    "How do achievements work?"
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
    handleSendMessage();
  };

  return (
    <div className="gamercat-container">
      {/* Chat button */}
      <button 
        className={`gamercat-button ${isOpen ? 'open' : ''} ${isDarkMode ? 'dark' : 'light'}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open GamerCat chatbot"
      >
        {isOpen ? '✕' : '🐱'}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className={`gamercat-window ${isDarkMode ? 'dark' : 'light'}`}>
          {/* Header */}
          <div className="gamercat-header">
            <div className="gamercat-header-content">
              <span className="gamercat-icon">🐱</span>
              <div>
                <h3>GamerCat</h3>
                <span className="gamercat-status">Online</span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="gamercat-messages">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`gamercat-message ${msg.type}`}
              >
                <div className="message-content">
                  {msg.text.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < msg.text.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                <span className="message-time">
                  {msg.timestamp.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick questions */}
          {messages.length <= 1 && (
            <div className="gamercat-quick-questions">
              <p>Quick questions:</p>
              {quickQuestions.map((question, index) => (
                <button 
                  key={index}
                  className="quick-question-btn"
                  onClick={() => handleQuickQuestion(question)}
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="gamercat-input-container">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="gamercat-input"
            />
            <button 
              onClick={handleSendMessage}
              className="gamercat-send-btn"
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamerCat;
